import { Component, DestroyRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';

import { combineLatest, of, switchMap, take, tap } from 'rxjs';

import { TmtLoggerService } from 'tmt-logger';

import { TargetEnvironment } from 'src/environments/environment.interfaces';
import { FileService } from 'src/services/file.service';
import { environment } from 'src/environments/environment';
import { EcuParam } from 'src/models/ecuParam';

import { FileParserService } from './../../../../services/file-parser.service';

/**
 * Displays ECU parameters for test run.
 */
@Component({
	selector: 'app-ecu-param',
	templateUrl: './ecu-param.component.html',
	styleUrls: ['./ecu-param.component.scss'],
})
export class EcuParamComponent {
	private destroyRef = inject(DestroyRef);

	// For old test runs the data was extracted into the database tables, which hold the
	// authoritative, complete set of E2 parameters (and the S3 file, if any, is only a thinner
	// snapshot). For newer uploads the database is empty and the full file lives in S3.
	// The database is therefore queried first; if it has no parameters, the S3 file is parsed.
	public fileData$ = combineLatest([this.fileService.testRunUid$, this.fileService.e2Files$]).pipe(
		switchMap(([testRunUid, files]) => {
			return this.fileService.getEcuParamsFromDb$(testRunUid).pipe(
				take(1),
				switchMap(dbItems => {
					if (dbItems.length) {
						this.setDataSource(dbItems, false);
						return of(dbItems);
					}

					// No data in the database: fall back to parsing the S3 file if one exists.
					if (!files || !files.length) {
						this.setDataSource([], true);
						return of([]);
					}

					return this.fileService.getFileByFileUid$(files[0].FileUid, files[0].FileName).pipe(
						switchMap(file => (file ? this.fileService.getFilestringFromFile$(file) : of(''))),
						tap(fileString => {
							const items = this.fileParserService.parseE2(fileString);
							this.setDataSource(items ?? [], true);
						}),
					);
				}),
			);
		}),
	);

	constructor(
		private fileService: FileService,
		private fileParserService: FileParserService,
		private loggerService: TmtLoggerService,
	) {
		// Subscribe to file data, since observables with Tegel are a pain. TODO: Replace with better alternative.
		this.fileData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	ecuParams: EcuParam[];

	displayedColumns: string[] = ['EcuFamily', 'EcuName', 'Name', 'Description', 'Value', 'EnumValue', 'ValueType'];

	displayedColumnsForFiles: string[] = ['name'];

	dataSource;

	dataSourceFiles;

	countEcuParamFiles: number;

	maxall = 10;

	baseUrl = environment.baseurl;

	environment = environment.environment;

	TargetEnvironment = TargetEnvironment;

	@ViewChild(MatPaginator) paginator: MatPaginator;

	@ViewChild(MatSort) sort: MatSort;

	@Output() countEcuParams: EventEmitter<any> = new EventEmitter();

	/** Emits true when the parameters were loaded from the database rather than parsed from an S3 file. */
	@Output() paramsFromDatabase: EventEmitter<boolean> = new EventEmitter();

	/**
	 * Binds parameters to the table and emits the count.
	 * @param items Parameters to display.
	 * @param fromFile Whether the parameters were parsed from an S3 file or loaded from the database.
	 */
	private setDataSource(items: EcuParam[], fromFile: boolean) {
		this.dataSource = new MatTableDataSource<EcuParam>(items);
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;

		this.countEcuParams.emit(items.length);
		this.paramsFromDatabase.emit(!fromFile);
	}

	public getPageSizeOptions(): number[] {
		if (this.dataSource && this.dataSource.paginator && this.dataSource.paginator.length > this.maxall) {
			return [10, 20, this.dataSource.paginator.length];
		} else {
			return [10, 20];
		}
	}

	public applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}
}
